/**
 * SettingsProductReviews — vendor moderation and settings for storefront product reviews.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, Loader2, Check, EyeOff, Eye, Trash2, Save, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import BackToSettings from './_BackToSettings';
import { themedConfirm } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_META = {
  published: { label: 'Published', color: 'text-emerald-300 bg-emerald-500/15 border-emerald-400/25', icon: Check },
  pending:   { label: 'Pending',   color: 'text-amber-300 bg-amber-500/15 border-amber-400/25',       icon: Loader2 },
  hidden:    { label: 'Hidden',    color: 'text-white/50 bg-white/5 border-white/10',                icon: EyeOff },
};

function StarRow({ rating }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((v) => (
        <Star key={v} size={12} className={v <= rating ? 'text-amber-400 fill-amber-400' : 'text-white/20'} />
      ))}
    </div>
  );
}

export default function SettingsProductReviews() {
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState({ enabled: true, auto_publish: true, require_verified_purchase: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [rev, cfg] = await Promise.all([
        axios.get(`${API}/product-reviews/list`, { headers: authH() }),
        axios.get(`${API}/product-reviews/settings`, { headers: authH() }),
      ]);
      setReviews(rev.data.reviews || []);
      setSettings({ ...settings, ...cfg.data });
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/product-reviews/settings`, settings, { headers: authH() });
      toast.success('Settings saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const setStatus = async (r, status) => {
    try {
      await axios.put(`${API}/product-reviews/${r.review_id}/status`, { status }, { headers: authH() });
      setReviews((arr) => arr.map((x) => (x.review_id === r.review_id ? { ...x, status } : x)));
      toast.success(`Review ${status}`);
    } catch { toast.error('Update failed'); }
  };

  const remove = async (r) => {
    if (!(await themedConfirm('Delete this review permanently?'))) return;
    try {
      await axios.delete(`${API}/product-reviews/${r.review_id}`, { headers: authH() });
      setReviews((arr) => arr.filter((x) => x.review_id !== r.review_id));
      toast.success('Review deleted');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="min-h-full" data-testid="settings-product-reviews-page">
      <div className="border-b border-white/[0.06] bg-white/[0.02] backdrop-blur-xl px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BackToSettings />
          <div className="w-px h-4 bg-white/10" />
          <h1 className="text-[13px] font-bold text-white uppercase tracking-[0.18em]">Product Reviews</h1>
        </div>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[11px] font-bold uppercase tracking-[0.14em] bg-[#A78BFA] text-[#0E0E23] hover:bg-white disabled:opacity-50"
          data-testid="reviews-save-settings"
        >
          <Save size={12} /> Save settings
        </button>
      </div>

      <div className="max-w-4xl mx-auto px-8 py-10 space-y-8">
        <div>
          <h2 className="text-[32px] font-bold text-white tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
            Product Reviews
          </h2>
          <p className="mt-3 text-[14px] text-[#9CA3AF] max-w-2xl">
            Let customers rate and review your products. Verified-purchase badges appear automatically when the reviewer&apos;s email matches a paid order.
          </p>
        </div>

        {/* Settings */}
        <div className="rounded-2xl border border-white/10 bg-[#15152E] p-5 space-y-3" data-testid="reviews-settings-card">
          {[
            { key: 'enabled',                   label: 'Enable product reviews', hint: 'Show the reviews section on product pages' },
            { key: 'auto_publish',              label: 'Auto-publish new reviews', hint: 'Otherwise reviews will be pending until you approve them' },
            { key: 'require_verified_purchase', label: 'Require verified purchase', hint: 'Only buyers with matching orders can leave reviews' },
          ].map((s) => (
            <label key={s.key} className="flex items-start gap-3 py-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!settings[s.key]}
                onChange={(e) => setSettings((c) => ({ ...c, [s.key]: e.target.checked }))}
                className="mt-1 w-4 h-4 accent-[#A78BFA]"
                data-testid={`review-toggle-${s.key}`}
              />
              <div>
                <p className="text-white text-sm font-bold">{s.label}</p>
                <p className="text-white/50 text-xs">{s.hint}</p>
              </div>
            </label>
          ))}
        </div>

        {/* Review list */}
        <div>
          <h3 className="text-white font-bold text-lg mb-3">All reviews {reviews.length > 0 && <span className="text-white/40 text-sm font-normal">({reviews.length})</span>}</h3>
          {loading && <div className="text-white/50 text-sm flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>}
          {!loading && reviews.length === 0 && (
            <div className="rounded-xl border border-white/8 bg-white/[0.02] p-8 text-center text-white/50">
              <Star size={24} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">No reviews yet</p>
            </div>
          )}
          <div className="space-y-3">
            {reviews.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.pending;
              return (
                <div key={r.review_id} className="p-4 rounded-xl border border-white/8 bg-white/[0.02]" data-testid={`vendor-review-${r.review_id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <StarRow rating={r.rating} />
                        <span className="text-white font-bold text-sm">{r.author_name || 'Anonymous'}</span>
                        {r.verified_purchase && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[9px] font-bold border border-emerald-400/25">
                            <ShieldCheck size={9} /> Verified
                          </span>
                        )}
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold border ${meta.color}`}>
                          <meta.icon size={9} /> {meta.label}
                        </span>
                      </div>
                      {r.title && <p className="mt-1.5 text-white/90 font-bold text-sm">{r.title}</p>}
                      <p className="mt-1 text-white/70 text-sm">{r.body}</p>
                      <p className="mt-2 text-[10px] text-white/40">Product {r.product_id} · {r.buyer_email || 'no email'} · {new Date(r.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {r.status !== 'published' && (
                        <button onClick={() => setStatus(r, 'published')} className="p-1.5 rounded text-emerald-300 hover:bg-emerald-500/15" title="Publish" data-testid={`review-publish-${r.review_id}`}><Eye size={13} /></button>
                      )}
                      {r.status !== 'hidden' && (
                        <button onClick={() => setStatus(r, 'hidden')} className="p-1.5 rounded text-white/50 hover:bg-white/5" title="Hide" data-testid={`review-hide-${r.review_id}`}><EyeOff size={13} /></button>
                      )}
                      <button onClick={() => remove(r)} className="p-1.5 rounded text-rose-300 hover:bg-rose-500/15" title="Delete" data-testid={`review-delete-${r.review_id}`}><Trash2 size={13} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
